import type { TrackReference, TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { TrackRefContext } from '@livekit/components-react';
import { getTrackReferenceId } from '@livekit/components-core';
import { ParticipantTile } from './participant/ParticipantTile';

/** @public */
export interface TrackLoopProps {
  /** Track references to loop over. You can the use `useTracks()` hook to get TrackReferences. */
  tracks: TrackReference[] | TrackReferenceOrPlaceholder[];
  /** Radius that will be applied to the ParticipantTile. */
  radius: string;
  /** Dimension for Participant's Profile Picture */
  profile_img_dimensions: string;
}

/**
 * The `TrackLoop` component loops over tracks. It is for example a easy way to loop over all participant camera and screen share tracks.
 * `TrackLoop` creates a `TrackRefContext` for each track that you can use to e.g. render the track.
 *
 * @example
 * ```tsx
 * const trackRefs = useTracks([Track.Source.Camera]);
 * <TrackLoop tracks={trackRefs} >
 *  <TrackRefContext.Consumer>
 *    {(trackRef) => trackRef && <VideoTrack trackRef={trackRef}/>}
 *  </TrackRefContext.Consumer>
 * <TrackLoop />
 * ```
 * @public
 */
export function TrackLoop({ tracks, radius, profile_img_dimensions }: TrackLoopProps) {
  return (
    <>
      {tracks.map((trackReference) => (
        <TrackRefContext.Provider
          value={trackReference}
          key={getTrackReferenceId(trackReference)}
        >
          <ParticipantTile radius={radius} profile_img_dimensions={profile_img_dimensions} />
        </TrackRefContext.Provider>
      ))}
    </>
  );
}

export function TrackLoopWithHostFocus({ tracks, radius, profile_img_dimensions }: TrackLoopProps) {
  const [hostTrack, second, third, fourth, fifth, sixth, seventh] = tracks;
  return (
    <div className="cl-host-focus-container">
      <div className="cl-two-columns">
        {[second, third].map((tr) => (
          <TrackRefContext.Provider
            value={tr}
            key={getTrackReferenceId(tr)}
          >
            <ParticipantTile radius={radius} profile_img_dimensions={profile_img_dimensions} />
          </TrackRefContext.Provider>
        ))}
      </div>

      <div className="cl-host">
        <TrackRefContext.Provider
          value={hostTrack}
          key={getTrackReferenceId(hostTrack)}
        >
          <ParticipantTile radius={radius} profile_img_dimensions={profile_img_dimensions} />
        </TrackRefContext.Provider>
      </div>

      <div className="cl-two-columns">
        {[fourth, fifth].map((tr) => (
          <TrackRefContext.Provider
            value={tr}
            key={getTrackReferenceId(tr)}
          >
            <ParticipantTile radius={radius} profile_img_dimensions={profile_img_dimensions} />
          </TrackRefContext.Provider>
        ))}
      </div>

      {sixth && seventh && (
        <div className="cl-two-columns">
          {[fourth, fifth].map((tr) => (
            <TrackRefContext.Provider
              value={tr}
              key={getTrackReferenceId(tr)}
            >
              <ParticipantTile radius={radius} profile_img_dimensions={profile_img_dimensions} />
            </TrackRefContext.Provider>
          ))}
        </div>
      )}
    </div>
  );
}
